import { useMarket } from '@ecosystem/market-context-selection';
import { useAnalytics, useAuthentication, useFeedback } from '@ecosystem/newron-client-lib';
import React, { useCallback, useContext, useEffect, useRef } from 'react';

import { NotificationToastType } from '../../../common';
import { UpsellContext } from '../../../state';
import { setIsModalOpen, setNotificationMessage } from '../../../state/action';
import { waitForElement } from '../../../utils';
import './ConsumerStoriesQualtricsSlide.css';
import { getMessageCode } from '../../consumer-slides-message/utils/getMessageCode';

const QualtricsFeedbackIdentifier = 'ConsumerSlidesQualtricsFeedbackIdentifier';
const QualtricsButtonsSelector = '.QSI__EmbeddedFeedbackContainer_TextButton';
const QualtricsSlideIndex = 4;

export const ConsumerStoriesQualtricsSlide = (): React.ReactElement => {
  const {
    dispatch,
    state: { isModalOpen, pageName, bannerNumber, activeSlideIndex },
  } = useContext(UpsellContext);
  const { extendEmbeddedData } = useFeedback();
  const { user } = useAuthentication();
  const { market } = useMarket();
  const { track } = useAnalytics();
  const slideIndex = useRef<number>(activeSlideIndex || 0);
  const bannerId = useRef<number | undefined>(bannerNumber || undefined);

  useEffect(() => {
    if (bannerNumber !== bannerId.current) {
      bannerId.current = bannerNumber;
    }
  }, [bannerNumber]);

  useEffect(() => {
    if (activeSlideIndex !== slideIndex.current) {
      slideIndex.current = activeSlideIndex;
    }
  }, [activeSlideIndex]);

  const eventClick = useCallback(
    button => {
      if (button?.title === 'Yes, I am interested') {
        track('upsell-lead-generated', {
          pageName,
          bannerName: getMessageCode(bannerId.current),
          slideNumber: slideIndex.current + 1,
        });
        dispatch(
          setNotificationMessage({
            message: 'Thank you for your request. We will be in touch with more information.',
            type: NotificationToastType.Success,
            timeout: 3000,
          })
        );
      } else {
        track('upsell-lead-not-generated', {
          pageName,
          bannerName: getMessageCode(bannerId.current),
          slideNumber: slideIndex.current + 1,
        });
      }
      dispatch(setIsModalOpen(false));
    },
    // eslint-disable-next-line
    [dispatch, track]
  );

  const handleClick = useCallback(async () => {
    const elementExists = await waitForElement(QualtricsButtonsSelector);
    if (elementExists) {
      const buttons = document.querySelectorAll(QualtricsButtonsSelector);
      buttons.forEach((button: HTMLElement) => {
        button.onclick = () => {
          eventClick(button);
        };
      });
    }
  }, [eventClick]);

  useEffect(() => {
    if (isModalOpen && activeSlideIndex === QualtricsSlideIndex) {
      extendEmbeddedData({
        market: `${market?.itemGroupId}/${market?.countryId}`,
        country: user?.country,
      });
      handleClick();
    }
  }, [extendEmbeddedData, handleClick, isModalOpen, market, user?.country, activeSlideIndex]);

  return (
    <div
      className="consumer-stories-qualtrics-slide text-left pt-0 px-8 pb-6"
      data-test-id="consumer-stories-qualtrics-slide"
    >
      <div className="consumer-stories-qualtrics-slide-content flex flex-column justify-center">
        <p className="text-center text-xl">
          Learn how{' '}
          <span className="font-bold">
            <span className="text-brand">gfk</span>newron Consumer
          </span>{' '}
          can maximize future shopper conversion for your brand!
        </p>
        <p className="text-center text-xl mb-2">
          Register your interest today and your GfK Account Manager will be in touch with more information.
        </p>
        {activeSlideIndex === QualtricsSlideIndex && (
          <div
            className="consumer-stories-qualtrics-slide-buttons flex justify-evenly"
            id={QualtricsFeedbackIdentifier}
            data-test-id="qualtrics-buttons"
          />
        )}
        <p className="consumer-stories-qualtrics-slide-contact text-md mb-2 mt-4 text-center">
          You can opt-out from receiving further communication on this by contacting us. Please refer to{' '}
          <a href="/privacy-policy" target="_blank">
            Privacy Policy
          </a>{' '}
          for more information.
        </p>
      </div>
    </div>
  );
};
